import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom'
import { AlertTriangle, RefreshCw, Home, ChevronDown } from 'lucide-react'
import { useState } from 'react'

function isChunkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return /loading chunk|failed to fetch dynamically imported module|dynamically imported/i.test(msg)
}

export default function RouteErrorPage() {
  const error    = useRouteError()
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)

  // ── Classify the error ───────────────────────────────────────────────────────

  const is404   = isRouteErrorResponse(error) && error.status === 404
  const isChunk = isChunkError(error)

  const title = is404
    ? 'Page not found'
    : isChunk
    ? 'Update available'
    : 'Something went wrong'

  const message = is404
    ? "The page you're looking for doesn't exist or was moved."
    : isChunk
    ? 'A new version of the app was deployed. Reload to get the latest.'
    : 'An unexpected error occurred. The team has been notified.'

  const stack = error instanceof Error
    ? error.stack ?? error.message
    : isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : String(error)

  // ── Layout ───────────────────────────────────────────────────────────────────

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 480 }}>

        {/* Icon + heading */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: is404 ? 'rgba(92,100,115,0.10)' : 'rgba(229,34,42,0.09)',
            border: `1px solid ${is404 ? 'var(--border)' : 'rgba(229,34,42,0.20)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AlertTriangle size={18} color={is404 ? 'var(--muted)' : '#e5222a'} />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)', letterSpacing: -0.4, lineHeight: 1.2 }}>
              {title}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 5, lineHeight: 1.5 }}>
              {message}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {isChunk || !is404 ? (
            <button
              className="btn-primary"
              onClick={() => window.location.reload()}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <RefreshCw size={13} />
              Reload page
            </button>
          ) : null}
          <button
            className="btn-ghost"
            onClick={() => navigate('/dashboard')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Home size={13} />
            Dashboard
          </button>
          {!is404 && !isChunk && (
            <button
              className="btn-ghost"
              onClick={() => navigate(-1)}
              style={{ marginLeft: 'auto' }}
            >
              Go back
            </button>
          )}
        </div>

        {/* Collapsible error details (non-404, non-chunk) */}
        {!is404 && !isChunk && (
          <div style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 8,
          }}>
            <button
              onClick={() => setExpanded((v) => !v)}
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--muted)', fontSize: 11, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.6,
                fontFamily: 'var(--font-sans)',
              }}
            >
              Error details
              <ChevronDown
                size={13}
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                }}
              />
            </button>
            {expanded && (
              <pre style={{
                margin: 0,
                padding: '0 14px 14px',
                fontSize: 10,
                lineHeight: 1.6,
                color: '#e5222a',
                fontFamily: 'var(--font-mono)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                borderTop: '1px solid var(--border)',
                paddingTop: 10,
                overflowX: 'auto',
                maxHeight: 220,
                overflowY: 'auto',
              }}>
                {stack}
              </pre>
            )}
          </div>
        )}

      </div>
    </div>
  )
}
